import TextFieldComponent from "@CubeComponents/Mui_Form/TextFieldComponent";
import SlickGrid1 from "@CubeComponents/html/SlickGrid1";
import type React from "react";
import type { SlickgridReactInstanceX, ToolbarItem, ValidatedColumnDefinition } from "@CubeComponents/html/SlickGrid1";
import { forwardRef, useRef, useState, type MutableRefObject } from "react";
import { useSelector } from "react-redux";
import { useAppDispatch as useDispatch } from "@Reducers/hooks";
import { useParams } from "react-router-dom";
import {
  ManualAdditionalCostState,
  deleteAdditionalCost,
  getEstimateAdditionalCostTotal,
  getEstimateNumberValueBasedOnSettings,
  manualCostSources,
  saveAdditionalCost,
} from "@Actions/estimation";
import { showToastNotification } from "@Reducers/toastSlice";
import { TOAST_TYPE_ERROR, TOAST_TYPE_SUCCESS } from "@Services/constants";
import { additionalCostsUrl } from "@Features/estimation/api/estimation.api";
import type { RootState } from "src/reducers";
import type { EstimateCurrency, EstimateData as EstimateDataState } from "@ServerTypes/estimates/estimates.schema";
import CentralManualCostComponent from "./CentralManualCostComponent";
import Text from "@CubeComponents/html/Text/Text";
import { Trans, useTranslation } from "@i18n";
import type { ObjectIdString } from "@Helpers/schemas/common.schema";

type AdditionalCostRecord = ManualAdditionalCostState & { value: number | string | null; uniqueId: string };

type SlickGridHandle = {
  dataView: {
    getAllSelectedItems: () => AdditionalCostRecord[];
  };
  refresh: () => void;
};

const AdditionalCostsList = forwardRef<
  SlickGridHandle,
  // The sole caller (EstimateCostDatabaseItemsGridNew) passes its own `currencyData` prop, which is
  // `EstimateCurrency` (estimates.schema.ts SSOT). Only `selectedSymbol.name` is read here.
  { currencyData: EstimateCurrency; disableToolbar?: boolean }
>(({ currencyData, disableToolbar = false }, ref) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  // Route params are the external entry point for these ids; the backend route schema validates them.
  const params = useParams<{ estimateId: ObjectIdString; versionId: ObjectIdString }>();

  const localRef = useRef<SlickGridHandle | null>(null);
  const dataGrid = (ref as MutableRefObject<SlickGridHandle | null>) || localRef;

  const estimateData = useSelector((state: RootState) => state.estimateData) as EstimateDataState;
  const valueFieldHeader = t("Value ({{currency}})", { currency: currencyData?.selectedSymbol?.name ?? "" });

  const [query] = useState({
    estimateId: params.estimateId,
    versionId: params.versionId,
  });
  const [toolbarButtonsDisabled, setToolbarButtonsDisabled] = useState({
    create: false,
    edit: true,
    delete: true,
  });

  const additionalCostListColumns = useRef([
    {
      field: "name",
      id: "name",
      name: t("Name"),
      textAlign: "Left",
    },
    {
      field: "value",
      id: "value",
      name: valueFieldHeader,
      textAlign: "Left",
      _valueGetter: (params: AdditionalCostRecord) => valueTemplate(params),
    },
  ]);

  const toolbarOptions = [
    {
      id: "create_additional_cost",
      prefixIcon: "add",
      name: t("Create"),
      align: "Left" as const,
      disabled: toolbarButtonsDisabled.create,
    },
    {
      id: "edit_additional_cost",
      prefixIcon: "editProfile",
      name: t("Edit"),
      align: "Left" as const,
      disabled: toolbarButtonsDisabled.edit,
    },
    {
      id: "delete_additional_cost",
      prefixIcon: "delete",
      name: t("Delete"),
      align: "Left" as const,
      disabled: toolbarButtonsDisabled.delete,
    },
  ];

  const closeAddModal = () => {
    setModalState({ show: false, currentData: null });
    setManualData(defaultData);
  };

  const refreshGrid = () => {
    dataGrid.current?.refresh();
  };

  async function toolbarClickHandler(toolClicked: string) {
    if (params.versionId) {
      dispatch(
        showToastNotification({
          content: t("This action is not allowed in preview mode"),
          type: TOAST_TYPE_ERROR,
        }),
      );
      return;
    } else if (dataGrid && dataGrid.current) {
      if (toolClicked === "create_additional_cost") {
        setModalState({ show: true, currentData: null });
      } else {
        const selectedRecords = dataGrid.current.dataView
          .getAllSelectedItems()
          .filter((record) => record && !(record as AdditionalCostRecord & { __isDummy?: boolean }).__isDummy);
        const firstRecord = selectedRecords[0];
        if (!firstRecord) {
          dispatch(showToastNotification({ content: t("Please select a row first"), type: TOAST_TYPE_ERROR }));
        } else if (toolClicked === "edit_additional_cost") {
          const { name, value, references, formula, uniqueId } = firstRecord;
          const currentData = {
            name,
            value,
            references: references || [],
            isReferenced: !!references?.length,
            formula: formula || "",
            id: uniqueId,
          };
          setModalState({
            show: true,
            currentData,
          });
          setManualData(currentData);
        } else if (toolClicked === "delete_additional_cost" && params.estimateId) {
          await deleteAdditionalCost({
            uniqueIds: selectedRecords.map((record) => record.uniqueId),
            estimateId: params.estimateId,
          });
          dispatch(getEstimateAdditionalCostTotal({ estimateId: params.estimateId }));
          refreshGrid();
        }
      }
    }
  }

  const selectingEventHandler = () => {
    if (dataGrid.current) {
      const selectedRecords = dataGrid.current.dataView
        .getAllSelectedItems()
        .filter((record) => record && !(record as AdditionalCostRecord & { __isDummy?: boolean }).__isDummy);
      setToolbarButtonsDisabled((prev) => ({
        ...prev,
        edit: selectedRecords.length !== 1,
        delete: selectedRecords.length == 0,
      }));
    }
  };

  function valueTemplate(params: AdditionalCostRecord) {
    if (params.value === null) {
      return "-";
    }
    return getEstimateNumberValueBasedOnSettings(Number(params.value), estimateData, "amount");
  }

  const content = () => (
    <div className="py-6 h-150">
      <SlickGrid1
        ref={dataGrid as typeof dataGrid & React.Ref<SlickgridReactInstanceX<AdditionalCostRecord>>}
        gridOptions={{
          enableAutoSizeColumns: true,
        }}
        disableMenuOptions
        rowHeight={45}
        columnDefinitions={
          additionalCostListColumns.current as typeof additionalCostListColumns.current &
            ValidatedColumnDefinition<AdditionalCostRecord>[]
        }
        remoteDataSource={additionalCostsUrl}
        query={query}
        toolbar={(!disableToolbar && toolbarOptions) as ToolbarItem[]}
        toolbarClickHandler={(!disableToolbar && toolbarClickHandler) as typeof toolbarClickHandler}
        onSelectedRowIdsChanged={(!disableToolbar && selectingEventHandler) as typeof selectingEventHandler}
        infiniteScroll
        enableFilteringOption
        enableSortingOption
        enableGroupingOption
        enableColumnChooser
        excelExport
      />
    </div>
  );

  const [modalState, setModalState] = useState<{ show: boolean; currentData: null | ManualAdditionalCostState }>({
    show: false,
    currentData: null,
  });
  const defaultData = {
    name: "",
    value: null,
    isReferenced: false,
    references: [],
    formula: "",
  };
  const [manualData, setManualData] = useState<ManualAdditionalCostState>(defaultData);

  const addModalBasicContent = () => (
    <div className="flex w-full gap-6">
      <div className="flex flex-1 flex-col gap-4">
        <Text className="font-semibold">{t("Name")}</Text>
        <TextFieldComponent
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            setManualData((prev) => ({ ...prev, name: e.target.value }))
          }
          defaultValue={manualData.name}
        />
      </div>
      <div className="flex flex-1 flex-col gap-4">
        <Text className="font-semibold">{t("Value")}</Text>
        <TextFieldComponent
          value={manualData.value || ""}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            setManualData((prev) => ({ ...prev, value: Number.parseFloat(e.target.value) }))
          }
          type="number"
          disabled={manualData.isReferenced}
        />
      </div>
    </div>
  );

  const saveClickHadler = async () => {
    if (!(manualData.name.trim() && Number.isFinite(manualData.value))) {
      return;
    }
    if (!params.estimateId) {
      return;
    }

    // At this point manualData.value is guaranteed to be a finite number (checked above)
    const response = await saveAdditionalCost({
      uniqueId: manualData.id,
      estimateId: params.estimateId,
      name: manualData.name,
      value: manualData.value as number,
      references: manualData.references,
      formula: manualData.formula,
    });

    if (response.data.status) {
      dispatch(
        showToastNotification({
          content: t("The additional cost has been saved"),
          type: TOAST_TYPE_SUCCESS,
        }),
      );
      dispatch(getEstimateAdditionalCostTotal({ estimateId: params.estimateId }));
      refreshGrid();
    } else {
      dispatch(
        showToastNotification({
          content: t("The additional cost could not be saved"),
          type: TOAST_TYPE_ERROR,
        }),
      );
    }

    closeAddModal();
  };

  const isSaveButtonDisabled = !(manualData.name.trim() && Number.isFinite(manualData.value));

  return (
    <div>
      {modalState.show && (
        <CentralManualCostComponent
          estimateId={params.estimateId!}
          initialData={modalState.currentData ?? undefined}
          manualData={manualData}
          source={manualCostSources.ADDITIONAL_COST}
          setManualData={setManualData}
          headerText={modalState.currentData ? t("Edit Additional Cost") : t("Add Additional Cost")}
          footerConfirmText={t("Save")}
          footerCancelText={t("Cancel")}
          upperContent={addModalBasicContent}
          referenceToggleLabel={t("Reference Existing Additional Costs")}
          noReferenceHeading1={<Text sx={{ whiteSpace: "normal" }}>{t("No Reference Additional Costs ")} </Text>}
          noReferenceHeading2={
            <Text sx={{ whiteSpace: "normal" }}>
              <Trans
                i18nKey="add_reference_text"
                components={{ bold: <span className="font-bold" /> }}
                values={{ name: "additional costs" }}
              />
            </Text>
          }
          addReferenceHeaderText={t("Add Reference")}
          formulaDetailToolTip={t("additionalCost_formula_icon_tooltip")}
          handleSave={saveClickHadler}
          handleCancel={closeAddModal}
          isSaveButtonDisabled={isSaveButtonDisabled}
        />
      )}
      {content()}
    </div>
  );
});
AdditionalCostsList.displayName = "AdditionalCostsList";
export default AdditionalCostsList;
